/**
 *  View module to be used for lists of things, for example, deployment lists, 
 *  devicegroup lists, etc. It's a table, with sortable (or not) columns.
 *
 *  Usage:
 *      var CollectionTableView = require('@/views/collection_table');
 *      var table_view = new CollectionTableView({
 *          template: template,
 *          collection: collection,
 *          childView: childView,
 *          default_sort_column: 0,
 *          default_sort_desc: true,
 *          secondary_sort: 'secondary_sort_attribute',
 *          empty_message: 'Empty',
 *          columns: [{
 *              name: 'name',
 *              class_name: 'class-name',
 *              sortable: true,
 *              sort_by: 'sorting_attribute',
 *              centered: false,
 *          }, {...}, {...},],
 *      });
 *      MasterView.showChildView('table_region', table_view);
 *  Arguments:
 *      template:
 *          Handlebars compiled string. OPTIONAL. If you want to ovverride the 
 *          table template, do it here.
 *      collection:
 *          V5 collection. The collection for which you want to display a 
 *          table. Should be a v5 collection
 *          public/js/collections/v5/collection.js
 *      childView:
 *          Marionette / Backbone View. IMPORTANT. Must be included, and the 
 *          template of the included view must have an equal number of `<td>` 
 *          as the number of columns in the `column` property (described below).
 *      default_sort_column:
 *          Number. Identify which of your columns should be the primary column 
 *          data is sorted upon. 
 *          table_view.columns[0] refers to the first item, 
 *          table_view.columns[1] refers to the second, etc
 *      secondary_sort:
 *          String. The attribute of a model in your collection upon which 
 *          secondary sorting should occur.
 *      default_sort_desc:
 *          Boolean. True for descending sort.
 *      empty_message:
 *          String. Will be displayed in lieu of an empty table.
 *      columns:
 *          IMPORTANT. An array containing one or more objects that describe 
 *          the contents of each column. The length of the array must equal the 
 *          number of `<td>` in the template of the childView.
 *          columns[i].name: String. The text that will display in this 
 *              column's header.
 *          columns[i].class_name: String. The html class that will be 
 *              applied to this column header.
 *          columns[i].sortable: Boolean. True to display UI and 
 *              functionality for allowing this column to be click-sorted.
 *          columns[i].sort_by: String. Must match the exact key of the 
 *              `attributes` property of a model in the `collections`
 *              property of the table. For example, if `collections` was 
 *              "deployments," you could have `sort_by` be 
 *              collections[0].attributes.sha, which would be the same as a 
 *              v5 deployment model `deployment.get('sha')`
 *          columns[i].centered: Boolean. True for centered text, false for 
 *              otherwise.
 *
 */


import _ from 'underscore';
import Marionette from 'backbone.marionette';
import Radio from 'backbone.radio';
import Handlebars from 'handlebars';
import EmptyTableRow from '@/views/empty_table_row';
import template from '@/../templates/collection-table.hbs';

var globalCh = Radio.channel('global');

/**
 * Handle a submit event, stop its default behavior
 * and then call an onSubmit function if the current
 * context includes it.
 */
function onSubmit(e) {
    e.preventDefault();
    if (this.onSubmit) this.onSubmit.call(e);
}

function add_sort_listener(view, column, event_obj) {
    if (!column.sortable) return;
    var sort_key = 'click th.' + column.class_name;
    var sort_val = function() {
        var secondary_sort_by = view.secondary_sort || '';
        // If we're already sorting by this field, switch direction
        // Otherwise, default to 'ascending'
        if (view.sort_field === column.class_name) {
            view.sort_asc = view.sort_asc ? false : true;
        } else {
            view.sort_field = column.class_name;
            view.sort_asc = true;
        }
        view.viewComparator = view.get_sort_function(
            column.sort_by, 
            secondary_sort_by
        );
        view.render();
    };
    event_obj[sort_key] = sort_val;
}
function add_help_listener(view, column, event_obj) {
    if (!column.help_command) return;
    var help_key = 'click .' + column.class_name + ' .help';
    var help_val = function() {
        globalCh.request(column.help_command);
    };
    event_obj[help_key] = help_val;
}
function add_select_all_listener(view, column, event_obj) {
    if (!column.select_all) return;
    var select_all_key = 
        'click .' 
        + column.class_name 
        + ' input:checkbox'
    ;
    var select_all_val = function(e) {
        // This is counter intuitive, but, think of it this way
        //  1. As soon as a checkbox is clicked, it's value
        //     become true, so the other checkboxes should follow
        //     suit.
        //  2. Vice versa, if it checkbox was checked, an then clicked
        //     we are now receiving the false value, and the others
        //     should follow suit.
        if (e.target.checked === true ){
            view.select_all();
        } else {
            view.deselect_all();
        }
    };
    event_obj[select_all_key] = select_all_val;
}

function add_tooltip(column) {
    if (!column.tooltip) return;
    // select th.column-classname .help, 
    // i.e. an element within the table header with a classname 'help'.
    // It's probably an <i> with a glyphicon class
    var selector = 'th.' + column.class_name + ' .help';
    this.$(selector)
        .attr({
            'data-toggle': 'tooltip',
            'data-placement': 'auto bottom',
            'title': column.tooltip
        })
        .tooltip({
            show: 500,
            hide: 100
        })
    ;
}
var CollectionTableView = Marionette.CompositeView.extend({
    ui : {
        thead : 'thead',
        form : '.collection-table-form-wrapper'
    },
    constructor: function(options) {
        this.onSubmit = options.onSubmit || null;
        Marionette.CompositeView.apply(this, arguments);
    },
    template: Handlebars.compile(template),
    className: 'content',
    childViewContainer: 'tbody',
    childViewOptions: function() {
        return {
            collection_table_view: this
        };
    },
    childViewEvents: {
        'child_selected': 'on_child_selected'
    },
    emptyView: EmptyTableRow,
    emptyViewOptions: function() {
        return {
            num_cols: this.options.columns && this.options.columns.length,
            message: this.options.empty_message || 'None'
        };
    },
    initialize: function(options) {
        this.childView = this.getOption('childView');

        // This is a bit insane. Default sort takes a table column, because 
        // the user controls it.
        // Secondary sort just takes an attribute or sortBy-style function, 
        // which allows non-column data to be used (like timestamps that 
        // are just pieces of secondary info in a column.)
        this.default_sort_column = options.columns[options.default_sort_column];
        this.secondary_sort = options.secondary_sort;

        if (this.default_sort_column) {
            this.sort_field = this.default_sort_column.class_name;
            this.sort_asc = !options.default_sort_desc;
            this.viewComparator = this.get_sort_function(
                this.default_sort_column.sort_by, 
                this.secondary_sort
            );
        }

        this.listenTo(this, 'before:render:empty', this.hide_table_header);
        this.listenTo(this, 'before:remove:empty', this.show_table_header);

        // Adding this here due to isEmpty not applying filters
        // This was a marionette PR that never made it into 2.4.4
        // https://github.com/marionettejs/backbone.marionette/pull/2458
        this.listenTo(this.collection, 'remove', function () {
            this.render();
        });

    },
    hide_table_header: function() {
        this.ui.thead.hide();
    },
    show_table_header: function() {
        this.ui.thead.show();
    },
    events: function() {
        var event_obj = {};

        // Prevent default behavior, but if the view has an
        // onSubmit handler for the form, use that
        event_obj['submit form'] = _.bind(onSubmit, this);

        _(this.options.columns).each(function(column) {
            add_sort_listener(this, column, event_obj);
            add_help_listener(this, column, event_obj);
            add_select_all_listener(this, column, event_obj);
        }, this);

        return event_obj;
    },
    templateContext: function() {
        return {
            class_name: this.options.class_name,
            columns: this.options.columns,
            sort_asc: this.sort_asc
        };
    },
    onRender: function() {
        var sort_dir = this.$('.' + this.sort_field + ' > .sort-dir');
        sort_dir.css('visibility', 'visible');
        // Add tooltips to columns
        this.options.columns.forEach(add_tooltip.bind(this));
    },
    get_selected_models: function() {
        var models = [];
        var collection = this.collection;
        if (this.ui.form) {
            this.ui.form.find('input:checkbox:checked').each(function(index, el){
                var model;
                var id = el.getAttribute('value');
                if (!id) return;
                model = collection.get(id);
                if (model) models.push(model);
            });
        }
        return models;
    },
    on_child_selected: function() {
        this.trigger('child_selected', this.get_selected_models());
    },
    deselect_all: function() {
        if (this.ui.form) {
            this.ui.form.find('input:checkbox:checked').prop('checked', false);
        }
        this.trigger('deselect_all', this.get_selected_models());
    },
    select_all: function() {
        if (this.ui.form) {
            this.ui.form.find('input:checkbox').prop('checked', true);
        }
        this.trigger('select_all', this.get_selected_models());
    },
    get_sort_function: function(primary_sort, secondary_sort) {
        var aIsLess = this.sort_asc ? -1 : 1;
        var bIsLess = this.sort_asc ? 1 : -1;

        var sort = function(a, b) {
            // We accept both model attribute keys and functions that 
            // return the data to be sorted
            var aField, aField2, bField, bField2;

            if (_.isFunction(primary_sort)) {
                aField = primary_sort.call(a);
                bField = primary_sort.call(b);
            } else {
                aField = a.get(primary_sort) || '';
                bField = b.get(primary_sort) || '';
            }

            if (_.isFunction(secondary_sort)) {
                aField2 = secondary_sort.call(a);
                bField2 = secondary_sort.call(b);
            } else {
                aField2 = a.get(secondary_sort) || '';
                bField2 = b.get(secondary_sort) || '';
            }

            if (aField.toLowerCase() === bField.toLowerCase()) {
                // Secondary sort is always ascending, since the user 
                // can't control it independently
                return (aField2.toLowerCase() < bField2.toLowerCase()) ? -1 : 1;
            } else {
                return (aField.toLowerCase() < bField.toLowerCase()) ? aIsLess : bIsLess;
            }
        };

        return sort;
    }
});

export default CollectionTableView;
